import React, { useState } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Icon from "components/atoms/Icon";
import { InfoIconButtonWrapper, InfoTooltipWrapper } from "./style";

export interface InfoIconButtonProps {
  /**
   * 툴팁에 표시할 내용 (텍스트 또는 React 컴포넌트)
   */
  tooltip: React.ReactNode;

  /**
   * 아이콘 이름 (IconNames에 정의된 이름)
   */
  iconName?: string;

  /**
   * 아이콘 크기
   */
  iconSize?: number;

  /**
   * 아이콘 색상
   */
  iconColor?: string;

  /**
   * 툴팁 위치 ('top', 'bottom', 'left', 'right')
   */
  tooltipPosition?: "top" | "bottom" | "left" | "right";

  /**
   * 툴팁 최대 너비
   */
  tooltipMaxWidth?: string;

  /**
   * 클릭 이벤트 핸들러
   */
  onClick?: () => void;

  /**
   * 마우스 진입 이벤트 핸들러
   */
  onMouseEnter?: () => void;

  /**
   * 마우스 이탈 이벤트 핸들러
   */
  onMouseLeave?: () => void;

  /**
   * 추가 CSS 클래스
   */
  className?: string;

  /**
   * 추가 스타일
   */
  style?: React.CSSProperties;
}

// 화살표의 위치(px) - style.ts의 &::before left/right/top/bottom 값과 맞춰야 함
const ARROW_OFFSET = 16;
const TOOLTIP_MARGIN = 12;

const InfoIconButton: React.FC<InfoIconButtonProps> = ({
  tooltip,
  iconName = "Information Circle",
  iconSize = 16,
  iconColor = COLOR_SYSTEM.get("Gray")[400],
  tooltipPosition = "bottom",
  tooltipMaxWidth = "400px",
  onClick,
  onMouseEnter,
  onMouseLeave,
  className,
  style,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [mousePos, setMousePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const handleMouseEnter = (e: React.MouseEvent) => {
    setShowTooltip(true);
    setMousePos({ x: e.clientX, y: e.clientY });
    onMouseEnter?.();
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
    onMouseLeave?.();
  };

  const handleClick = () => {
    setShowTooltip(!showTooltip);
    onClick?.();
  };

  // 툴팁 위치 계산: 화살표의 끝이 마우스 포인터와 일치하도록 offset 적용
  let tooltipStyle: React.CSSProperties = {
    position: "fixed",
    pointerEvents: "none",
    zIndex: 9999,
    maxWidth: tooltipMaxWidth,
  };
  if (showTooltip) {
    switch (tooltipPosition) {
      case "bottom":
        tooltipStyle.left = mousePos.x - ARROW_OFFSET;
        tooltipStyle.top = mousePos.y + TOOLTIP_MARGIN;
        break;
      case "top":
        tooltipStyle.left = mousePos.x - ARROW_OFFSET;
        tooltipStyle.top = mousePos.y - TOOLTIP_MARGIN;
        tooltipStyle.transform = "translateY(-100%)";
        break;
      case "left":
        tooltipStyle.left = mousePos.x - TOOLTIP_MARGIN;
        tooltipStyle.top = mousePos.y - ARROW_OFFSET;
        tooltipStyle.transform = "translateX(-100%)";
        break;
      case "right":
        tooltipStyle.left = mousePos.x + TOOLTIP_MARGIN;
        tooltipStyle.top = mousePos.y - ARROW_OFFSET;
        break;
      default:
        tooltipStyle.left = mousePos.x - ARROW_OFFSET;
        tooltipStyle.top = mousePos.y + TOOLTIP_MARGIN;
    }
  }

  return (
    <>
      <InfoIconButtonWrapper
        className={className}
        style={style}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Icon name={iconName} size={iconSize} color={iconColor} />
      </InfoIconButtonWrapper>
      {showTooltip &&
        typeof window !== "undefined" &&
        ReactDOM.createPortal(
          <InfoTooltipWrapper
            show={showTooltip}
            position={tooltipPosition}
            maxWidth={tooltipMaxWidth}
            style={tooltipStyle}
          >
            {tooltip}
          </InfoTooltipWrapper>,
          document.body
        )}
    </>
  );
};

export default InfoIconButton;
